import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * ImpactMetrics (formerly LearnerMetrics)
 *
 * Generic primary-metric tracker. Each cohort optionally configures a
 * targets.primaryMetric object with a custom label (e.g. "Active learners",
 * "Active users", "Hectares under management"). If a cohort has no
 * primaryMetric configured, this component should not be rendered at all —
 * that decision is made by the caller, not by this component.
 */
export default function LearnerMetrics({ cohortId, cohortIds, isEditable = false }) {
  const cohortIdsKey = JSON.stringify(cohortIds || []);
  const [companies, setCompanies]   = useState([]);
  const [workPlans, setWorkPlans]   = useState([]);
  const [target, setTarget]         = useState(0);
  const [metricLabel, setMetricLabel] = useState('Active learners');
  const [metricKey, setMetricKey]   = useState('Number of active learners this period');
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDraft, setTargetDraft] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [hasMetric, setHasMetric]   = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const effectiveCohortId = cohortId || (cohortIds && cohortIds[0]);
        if (effectiveCohortId) {
          const cohortDoc = await getDoc(doc(db, 'cohorts', effectiveCohortId));
          const pm = cohortDoc.data()?.targets?.primaryMetric;
          if (pm?.avgPerCompany) {
            setTarget(pm.avgPerCompany);
            setTargetDraft(pm.avgPerCompany);
            setMetricLabel(pm.label || 'Active learners');
            setMetricKey(pm.workPlanMetricKey || 'Number of active learners this period');
            setHasMetric(true);
          }
        }

        const cSnap = await getDocs(collection(db, 'companies'));
        let comps = cSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (cohortId) {
          comps = comps.filter(c => c.cohortId === cohortId);
        } else if (cohortIds && cohortIds.length > 0) {
          comps = comps.filter(c => cohortIds.includes(c.cohortId));
        }
        setCompanies(comps);

        const allPlans = [];
        for (const comp of comps) {
          const wpSnap = await getDocs(collection(db, 'companies', comp.id, 'workPlans'));
          wpSnap.docs.forEach(d => {
            const data = d.data();
            if (data.metrics) allPlans.push({ ...data, companyId: comp.id, companyName: comp.name });
          });
        }
        const latestByCompany = {};
        allPlans.forEach(p => {
          const existing = latestByCompany[p.companyId];
          const pDate = p.updatedAt?.toDate ? p.updatedAt.toDate() : new Date(p.updatedAt || 0);
          const eDate = existing?.updatedAt?.toDate ? existing.updatedAt.toDate() : new Date(0);
          if (!existing || pDate > eDate) latestByCompany[p.companyId] = p;
        });
        setWorkPlans(Object.values(latestByCompany));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [cohortId, cohortIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTarget = async () => {
    const effectiveCohortId = cohortId || (cohortIds && cohortIds[0]);
    if (!effectiveCohortId) return;
    try {
      await updateDoc(doc(db, 'cohorts', effectiveCohortId), {
        'targets.primaryMetric.avgPerCompany': parseInt(targetDraft),
        'targets.primaryMetric.label': metricLabel,
        'targets.primaryMetric.workPlanMetricKey': metricKey,
        'targets.primaryMetric.updatedAt': new Date(),
      });
      setTarget(parseInt(targetDraft));
      setHasMetric(true);
      setEditingTarget(false);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  // No metric configured and not in edit mode — render nothing (caller should also gate this)
  if (!hasMetric && !editingTarget) {
    if (!isEditable) return null;
  }

  const learnerData = companies.map(c => {
    const plan = workPlans.find(w => w.companyId === c.id);
    const count = plan?.metrics?.[metricKey];
    return {
      id: c.id, name: c.name,
      value: count ? parseInt(String(count).replace(/,/g, '')) || 0 : null,
      hasData: !!count,
    };
  });

  const companiesWithData = learnerData.filter(c => c.hasData);
  const totalValue = learnerData.reduce((sum, c) => sum + (c.value || 0), 0);
  const avgValue    = companiesWithData.length > 0 ? Math.round(totalValue / companiesWithData.length) : 0;
  const totalTarget = target * companies.length;
  const avgPct       = target > 0 ? Math.round((avgValue / target) * 100) : 0;
  const totalPct     = totalTarget > 0 ? Math.round((totalValue / totalTarget) * 100) : 0;

  return (
    <div className="gm-learner-metrics">
      <div className="gm-learner-header">
        <h2 className="gm-section-title" style={{ marginBottom: 0 }}>{metricLabel}</h2>
        {isEditable && (
          editingTarget ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" placeholder="Metric label (e.g. Active learners, Active farmers)"
                value={metricLabel} onChange={e => setMetricLabel(e.target.value)}
                style={{ padding: '6px 10px', border: '1.5px solid #0369a1', borderRadius: 6, fontSize: '0.82rem', width: 220 }} />
              <input type="number" placeholder="Target per company" value={targetDraft}
                onChange={e => setTargetDraft(e.target.value)}
                style={{ width: 120, padding: '6px 10px', border: '1.5px solid #0369a1', borderRadius: 6, fontSize: '0.875rem' }}
              />
              <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={saveTarget}>Save target</button>
              <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setEditingTarget(false)}>Cancel</button>
            </div>
          ) : (
            <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setEditingTarget(true)}>
              {hasMetric ? 'Edit target' : '+ Set up metric tracking'}
            </button>
          )
        )}
      </div>

      {!hasMetric && !editingTarget && (
        <div className="gm-empty-state">
          <p>No primary metric configured for this cohort yet. Click "Set up metric tracking" to define one (e.g. active learners, active users, hectares under management).</p>
        </div>
      )}

      {hasMetric && (
        <>
          <div className="gm-kpi-row" style={{ marginTop: 16 }}>
            <div className="gm-kpi-card">
              <div className="gm-kpi-value">{totalValue.toLocaleString()}</div>
              <div className="gm-kpi-label">Total {metricLabel.toLowerCase()}</div>
              <div className="gm-kpi-sub">Target: {totalTarget.toLocaleString()} · {totalPct}% of target</div>
              <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                <div className="gm-mini-progress-fill"
                  style={{ width: `${Math.min(totalPct, 100)}%`, background: totalPct >= 75 ? '#166534' : totalPct >= 50 ? '#0369a1' : '#dc2626' }} />
              </div>
            </div>
            <div className="gm-kpi-card gm-kpi-card--accent">
              <div className="gm-kpi-value">{avgValue.toLocaleString()}</div>
              <div className="gm-kpi-label">Avg per company</div>
              <div className="gm-kpi-sub">Target: {target.toLocaleString()} · {avgPct}% of target</div>
              <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                <div className="gm-mini-progress-fill"
                  style={{ width: `${Math.min(avgPct, 100)}%`, background: avgPct >= 75 ? '#166534' : avgPct >= 50 ? '#0369a1' : '#dc2626' }} />
              </div>
            </div>
            <div className="gm-kpi-card">
              <div className="gm-kpi-value">{companiesWithData.length}<span style={{ fontSize: '1rem', fontWeight: 400 }}>/{companies.length}</span></div>
              <div className="gm-kpi-label">Companies reporting</div>
            </div>
          </div>

          <div className="gm-table-wrap" style={{ marginTop: 16 }}>
            <table className="gm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>{metricLabel}</th>
                  <th>vs target ({target.toLocaleString()})</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {learnerData
                  .sort((a,b) => (b.value||0) - (a.value||0))
                  .map(c => {
                    const pct = c.hasData && target > 0 ? Math.round((c.value / target) * 100) : null;
                    const color = pct === null ? '#94a3b8' : pct >= 75 ? '#166534' : pct >= 50 ? '#0369a1' : '#dc2626';
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td style={{ fontWeight: 700, color }}>
                          {c.hasData ? c.value.toLocaleString() : <span style={{ color: '#94a3b8' }}>Not reported</span>}
                        </td>
                        <td>
                          {pct !== null && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="gm-mini-progress" style={{ width: 80 }}>
                                <div className="gm-mini-progress-fill"
                                  style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                              </div>
                              <span style={{ fontSize: '0.75rem', color }}>{pct}%</span>
                            </div>
                          )}
                        </td>
                        <td>
                          {!c.hasData && <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Awaiting data</span>}
                          {pct !== null && pct >= 75 && <span className="gm-validated-badge">On track</span>}
                          {pct !== null && pct >= 50 && pct < 75 && <span className="gm-pending-badge" style={{ background: '#eff6ff', color: '#0369a1', borderColor: '#93c5fd' }}>Progressing</span>}
                          {pct !== null && pct < 50 && <span className="gm-pending-badge" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}>Needs attention</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
